import { useEffect, useRef, useState } from 'react';

interface Options {
  /** Tween duration in ms. Default 600. */
  durationMs?: number;
  /** Easing — defaults to easeOutCubic for the snappy-then-settle feel. */
  easing?: (t: number) => number;
}

interface SmoothOptions {
  /**
   * Per-frame interpolation factor (0..1). Smaller = smoother +
   * slower. 0.10 ≈ ~120ms to settle (60fps), 0.05 ≈ ~250ms.
   * Default 0.10.
   */
  alpha?: number;
  /**
   * Stop the RAF loop once the displayed value is within this many
   * USD of the target. Default 0.005 (half a cent).
   */
  epsilon?: number;
}

const DEFAULT_DURATION = 600;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/**
 * Smoothly interpolate `value` from its previous value over `durationMs`
 * using `requestAnimationFrame`. Returns a number that re-renders at
 * roughly the display refresh rate.
 *
 * Pauses the tween while `document.hidden` so a backgrounded WebApp tab
 * doesn't keep RAF burning battery — when visibility returns, we snap to
 * the latest target instead of replaying the missed frames.
 */
export function useAnimatedNumber(value: number, options: Options = {}): number {
  const { durationMs = DEFAULT_DURATION, easing = easeOutCubic } = options;
  const [displayed, setDisplayed] = useState(value);
  const fromRef = useRef(value);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const targetRef = useRef(value);

  useEffect(() => {
    targetRef.current = value;
    fromRef.current = displayed;
    startRef.current = null;

    if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
      setDisplayed(value);
      return;
    }
    if (typeof document !== 'undefined' && document.hidden) {
      setDisplayed(value);
      return;
    }
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setDisplayed(value);
      return;
    }
    if (fromRef.current === value) return;

    const step = (now: number) => {
      if (startRef.current === null) startRef.current = now;
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / durationMs);
      const eased = easing(t);
      const next = fromRef.current + (targetRef.current - fromRef.current) * eased;
      setDisplayed(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
    // `displayed` intentionally excluded — we read it once at tween start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, durationMs, easing]);

  return displayed;
}

/**
 * Continuous low-pass filter for high-frequency streams (price tickers).
 *
 * Unlike the tween hook, every frame moves the displayed value a
 * fraction of the way toward the latest target. New `value` updates
 * smoothly merge with the in-flight motion instead of restarting an
 * easing curve, so a ticker firing every 250ms feels like one
 * continuous slide instead of jittery hops.
 */
export function useSmoothNumber(value: number, options: SmoothOptions = {}): number {
  const { alpha = 0.1, epsilon = 0.005 } = options;
  const [displayed, setDisplayed] = useState(value);
  const targetRef = useRef(value);
  const displayRef = useRef(value);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    targetRef.current = value;
    if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
      displayRef.current = value;
      setDisplayed(value);
      return;
    }
    if (typeof document !== 'undefined' && document.hidden) {
      displayRef.current = value;
      setDisplayed(value);
      return;
    }
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      displayRef.current = value;
      setDisplayed(value);
      return;
    }

    if (rafRef.current !== null) return; // loop already running

    const step = () => {
      const t = targetRef.current;
      const d = displayRef.current;
      const next = d + (t - d) * alpha;
      // Snap when within epsilon — stops the RAF loop, saves battery.
      if (Math.abs(t - next) < epsilon) {
        displayRef.current = t;
        setDisplayed(t);
        rafRef.current = null;
        return;
      }
      displayRef.current = next;
      setDisplayed(next);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [value, alpha, epsilon]);

  return displayed;
}
